<template>
  <div class="register-container">
    <div class="register-card">
      <div class="register-header">
        <img src="../assets/logo.png" alt="Logo" class="register-logo">
        <h2 class="register-title">用户注册</h2>
      </div>
      
      <el-form :model="registerForm" :rules="registerRules" ref="registerForm" class="register-form">
        <el-form-item prop="username">
          <el-input 
            v-model="registerForm.username" 
            prefix-icon="el-icon-user" 
            placeholder="请输入用户名"
            clearable>
          </el-input>
        </el-form-item>
        
        <el-form-item prop="realName">
          <el-input 
            v-model="registerForm.realName" 
            prefix-icon="el-icon-postcard" 
            placeholder="请输入名称"
            clearable>
          </el-input>
        </el-form-item>

        <el-form-item prop="phone">
          <el-input 
            v-model="registerForm.phone" 
            prefix-icon="el-icon-phone" 
            placeholder="请输入手机号码"
            clearable>
          </el-input>
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input 
            v-model="registerForm.password" 
            prefix-icon="el-icon-lock" 
            placeholder="请输入密码" 
            type="password"
            show-password>
          </el-input>
        </el-form-item>
        
        <el-form-item prop="confirmPassword">
          <el-input 
            v-model="registerForm.confirmPassword" 
            prefix-icon="el-icon-lock" 
            placeholder="请确认密码" 
            type="password"
            show-password>
          </el-input>
        </el-form-item>
        
        <el-form-item>
          <el-button 
            type="primary" 
            class="register-button" 
            :loading="loading" 
            @click="handleRegister">
            注册
          </el-button>
        </el-form-item>
        
        <div class="register-options">
          <span class="login-link" @click="goToLogin">已有账号？立即登录</span>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import { register } from '@/api/auth'
import { encryptPassword } from '@/utils/password'

export default {
  name: 'UserRegister',
  data() {
    // 密码确认验证
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.registerForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    
    // 手机号验证
    const validatePhone = (rule, value, callback) => {
      if (value && !/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号码'))
      } else {
        callback()
      }
    }
    
    return {
      registerForm: {
        username: '',
        email: '',
        realName: '',
        phone: '',
        password: '',
        confirmPassword: ''
      },
      registerRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在3到20个字符之间', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱地址', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: ['blur', 'change'] }
        ],
        realName: [
          { required: true, message: '请输入名称', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入手机号码', trigger: 'blur' },
          { validator: validatePhone, trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度必须在6到20个字符之间', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      loading: false
    }
  },
  methods: {
    handleRegister() {
      this.$refs.registerForm.validate(async valid => {
        if (valid) {
          // 额外校验密码长度
          if (this.registerForm.password.length < 6 || this.registerForm.password.length > 20) {
            this.$message.error('密码长度必须在6到20个字符之间');
            return;
          }
          
          // 确认密码必须匹配
          if (this.registerForm.password !== this.registerForm.confirmPassword) {
            this.$message.error('两次输入的密码不一致');
            return;
          }
          
          this.loading = true
          try {
            // 剔除确认密码字段
            const {  ...registerData } = this.registerForm
            
            // 加密密码
            const originalPassword = registerData.password
            registerData.password = encryptPassword(originalPassword)
            
            // 调用注册API
            const response = await register(registerData)
            
            // 检查API返回值格式
            if (response.code === 200) {
              this.$message({
                message: '注册成功，请登录',
                type: 'success'
              })
              
              // 注册成功后跳转到登录页
              this.$router.push('/login')
            } else {
              throw new Error(response.message || '注册失败')
            }
          } catch (error) {
            console.error(error)
            this.$message({
              message: error.message || '注册失败，请稍后再试',
              type: 'error'
            })
          } finally {
            this.loading = false
          }
        }
      })
    },
    goToLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(to right bottom, #2c3e50, #4ca1af);
}

.register-card {
  width: 400px;
  padding: 40px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.register-header {
  text-align: center;
  margin-bottom: 30px;
}

.register-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 15px;
}

.register-title {
  color: #303133;
  font-weight: 500;
  margin: 0;
  font-size: 24px;
}

.register-form {
  margin-top: 30px;
}

.register-button {
  width: 100%;
  padding: 12px 0;
}

.register-options {
  text-align: center;
  margin-top: 15px;
}

.login-link {
  color: #409EFF;
  font-size: 14px;
  cursor: pointer;
}
</style> 